import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function CancellationPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-4">Cancellation & Refund Policy</h1>
        <p className="text-zinc-400 mb-8">Last updated: January 8, 2025</p>

        <div className="prose prose-invert max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Cancellation by Customer</h2>
            <p className="text-zinc-300 mb-4">
              We understand that plans can change. Our cancellation policy is designed to be fair to both our customers and our business operations.
            </p>
            
            <h3 className="text-xl font-semibold text-white mb-3">1.1 Cancellation Timeline & Refunds</h3>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-4">
              <ul className="space-y-4 text-zinc-300">
                <li className="flex items-start gap-3">
                  <span className="text-green-400 font-bold min-w-[120px]">24+ hours:</span>
                  <span><strong>100% refund</strong> - Full refund if cancelled more than 24 hours before scheduled pickup</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold min-w-[120px]">Less than 24h:</span>
                  <span><strong>No refund</strong> - No refund for cancellations within 24 hours of scheduled pickup</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. How to Cancel</h2>
            <p className="text-zinc-300 mb-4">
              To cancel your booking, please contact us immediately:
            </p>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-4">
              <ul className="space-y-2 text-zinc-300">
                <li>📞 Phone: <a href="tel:+306974994577" className="text-amber-400 hover:text-amber-300">+30 697 499 4577</a> (Fastest method)</li>
                <li>📧 Email: <a href="mailto:giorgos8956@gmail.com" className="text-amber-400 hover:text-amber-300">giorgos8956@gmail.com</a></li>
              </ul>
              <p className="text-zinc-400 text-sm mt-4">
                Please include your booking ID and confirmation details when contacting us.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Modifications to Booking</h2>
            <p className="text-zinc-300 mb-4">
              If you need to modify your booking (change date, time, or location):
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li><strong>Free modifications:</strong> Changes made more than 24 hours in advance</li>
              <li><strong>Subject to availability:</strong> We will do our best to accommodate your changes</li>
              <li><strong>Price adjustments:</strong> If the modified booking has a different distance or duration, pricing will be adjusted accordingly</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Cancellation by Santorini Best Experience</h2>
            <p className="text-zinc-300 mb-4">
              We reserve the right to cancel services in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li>Severe weather conditions that make travel unsafe</li>
              <li>Vehicle breakdown or technical issues</li>
              <li>Force majeure events (natural disasters, strikes, etc.)</li>
              <li>Customer provides false or misleading information</li>
            </ul>
            <p className="text-zinc-300 mb-4">
              <strong>In these cases, you will receive a 100% refund or the option to reschedule.</strong>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. No-Show Policy</h2>
            <div className="bg-red-900/20 border border-red-800 rounded-xl p-6 mb-4">
              <p className="text-zinc-300 mb-2">
                <strong className="text-red-400">No refund</strong> will be provided if:
              </p>
              <ul className="list-disc list-inside text-zinc-300 space-y-2">
                <li>Customer is not at the pickup location at the scheduled time</li>
                <li>Customer cannot be contacted via provided phone number</li>
                <li>Customer fails to notify us of delays or changes</li>
              </ul>
              <p className="text-zinc-400 text-sm mt-4">
                We wait 15 minutes after scheduled pickup time. Please ensure you're on time or contact us if running late.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Refund Processing</h2>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li><strong>Processing time:</strong> Refunds are processed within 5-10 business days</li>
              <li><strong>Method:</strong> Refunds are issued to the original payment method used</li>
              <li><strong>Notification:</strong> You will receive an email confirmation once the refund is processed</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Special Circumstances</h2>
            <h3 className="text-xl font-semibold text-white mb-3">7.1 Flight/Ferry Delays</h3>
            <p className="text-zinc-300 mb-4">
              If your flight or ferry is delayed:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li>Contact us as soon as possible with updated arrival time</li>
              <li>We will do our best to accommodate the delay at no extra charge</li>
              <li>If we cannot accommodate, we will offer rescheduling or full refund</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">7.2 Medical Emergencies</h3>
            <p className="text-zinc-300 mb-4">
              In case of medical emergencies or serious illness, we may offer flexible cancellation terms. Please contact us with documentation.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Disputes</h2>
            <p className="text-zinc-300 mb-4">
              If you have any concerns about cancellations or refunds, please contact us first. We are committed to resolving any issues fairly and promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Contact Us</h2>
            <p className="text-zinc-300 mb-4">
              For cancellations, modifications, or questions:
            </p>
            <div className="text-zinc-300 space-y-2">
              <p><strong>Santorini Best Experience</strong></p>
              <p>Akrotiri, Santorini, Greece</p>
              <p>Phone: <a href="tel:+306974994577" className="text-amber-400 hover:text-amber-300">+30 697 499 4577</a></p>
              <p>Email: <a href="mailto:giorgos8956@gmail.com" className="text-amber-400 hover:text-amber-300">giorgos8956@gmail.com</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
